import { Auth } from 'aws-amplify';
import {
  CognitoUser,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserSession,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

export const ADMIN_REFRESH_REQUEST_MESSAGE_TYPE = 'LOREE_OPENEDX_ADMIN_REFRESH_REQUEST';
export const ADMIN_REFRESH_RESPONSE_MESSAGE_TYPE = 'LOREE_OPENEDX_ADMIN_REFRESH_RESPONSE';

const REFRESH_TIMEOUT_MS = 15000;
const ADMIN_USERNAME_KEY = 'loreeAdminUsername';

export const getAmplifyUserPool = (): CognitoUserPool =>
  (Auth as unknown as { userPool: CognitoUserPool }).userPool;

export const getExpectedParentOrigin = (): string | null => {
  try {
    return document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    return null;
  }
};

export const rememberAdminUsername = (username: string): void => {
  sessionStorage.setItem(ADMIN_USERNAME_KEY, username);
};

let inFlightRefresh: Promise<string> | null = null;

export async function requestAdminTokenRefresh(): Promise<string> {
  if (inFlightRefresh) return await inFlightRefresh;

  inFlightRefresh = new Promise<string>((resolve, reject) => {
    const username = sessionStorage.getItem(ADMIN_USERNAME_KEY) || '';
    const expectedOrigin = getExpectedParentOrigin();
    if (!username || !expectedOrigin || window.parent === window) {
      reject(new Error('Cannot refresh admin session: missing host context.'));
      return;
    }

    const timer = setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      reject(new Error('Admin token refresh timed out.'));
    }, REFRESH_TIMEOUT_MS);

    function handleMessage(event: MessageEvent): void {
      if (event.source !== window.parent || event.origin !== expectedOrigin) return;
      const data = event.data as
        | {
            type?: string;
            cognitoToken?: string;
            cognitoAccessToken?: string;
          }
        | undefined;
      if (data?.type !== ADMIN_REFRESH_RESPONSE_MESSAGE_TYPE) return;

      clearTimeout(timer);
      window.removeEventListener('message', handleMessage);

      if (!data.cognitoToken || !data.cognitoAccessToken) {
        reject(new Error('Admin token refresh response missing tokens.'));
        return;
      }

      try {
        const cognitoUser = new CognitoUser({
          Username: username,
          Pool: getAmplifyUserPool(),
          Storage: window.localStorage,
        });
        const session = new CognitoUserSession({
          IdToken: new CognitoIdToken({ IdToken: data.cognitoToken }),
          AccessToken: new CognitoAccessToken({ AccessToken: data.cognitoAccessToken }),
          RefreshToken: new CognitoRefreshToken({ RefreshToken: '' }),
        });
        cognitoUser.setSignInUserSession(session);
        localStorage.setItem('ReactAmplify.TokenKey', data.cognitoToken);
        resolve(data.cognitoToken);
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    }

    window.addEventListener('message', handleMessage);
    window.parent.postMessage({ type: ADMIN_REFRESH_REQUEST_MESSAGE_TYPE }, expectedOrigin);
  }).finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}
